import React, { useState, useEffect, useMemo, Fragment, useRef } from 'react';
import { Checkbox, Icon } from '@haldor/ui';
import { useDispatch, useSelector } from 'react-redux';
import { translate as getTranslate } from '@haldor/ui';
import DropDownList from '../DropDownList';
import {
	setAllGroups,
} from 'actions/ScheduleFilter';
import { getMyEducationGroups, getGroupById } from 'actions/groups';
import { Collapsible, Spinner } from 'UI';
import { Minus, Plus } from 'UI/Icons';
import DisplayName from 'components/Presentation/DisplayName';
import SearchGroups from './SearchGroups';

const GroupsAndUserSelector = ({ scheduleLoading }) => {
	const [groups, setGroups] = useState(null);
	const groupsRef = useRef(groups);

	const [isLoading, setIsloading] = useState(true);

	const scheduleGroups = useSelector((state) => state.ScheduleFilter.groups);

	const dispatch = useDispatch();
	const translate = getTranslate(useSelector((state) => state.Languages.translations));

	const storeGroupsInLocalStorage = (groups) => {
		let minifiedGroups = simplifyGroups(groups);
		localStorage.setItem('scheduleView_filter_groups', JSON.stringify(minifiedGroups));
	}


	/**
	 * We need to use the groupsRef to able to access the groups in the cleanup function 
	 */
	useEffect(() => {

		//A timer is started with a 800 delay using setTimeout. 
		//This delay acts like a debounce, allowing the code to wait before performing the update. 
		//It prevents immediate state updates when groups changes rapidly
		const timer = setTimeout(() => {
			if (groups != null && groups.some(group => group.isLoading)) {
				return;
			}

			groupsRef.current = groups;
			storeGroupsInLocalStorage(groupsRef.current);
			dispatch(setAllGroups(groupsRef.current));
		}, 800);

		return () => clearTimeout(timer);
	}, [groups]);

	/**
	 * Fetches the groups from scheduleFilter if exists or fetches from the getMyEducationGroups
	 */
	const fetchGroups = async () => {
		if (scheduleGroups == null || scheduleGroups.length === 0) {
			setIsloading(true);

			var groups = await dispatch(getMyEducationGroups());
			if (groups) {
				var fetchedGroups = JSON.parse(JSON.stringify(groups));

				var localStorageGroups = localStorage.getItem('scheduleView_filter_groups');
				localStorageGroups = JSON.parse(localStorageGroups);

				if (localStorageGroups && Array.isArray(localStorageGroups) && localStorageGroups.length > 0) {
					const groupPromises = fetchedGroups.map(async (group) => {
						const localStorageGroup = localStorageGroups.find((g) => g.id === group.id);
						if (localStorageGroup != null && (localStorageGroup.members != null && localStorageGroup.members.some((m) => m.selected)) || (localStorageGroup.owners != null && localStorageGroup.owners.some((o) => o.selected))) {
							var fetchedGroup = await dispatch(getGroupById(group.id, ['owners', 'members']));

							group.members = fetchedGroup.members.map((member) => {
								const localStorageMember = localStorageGroup.members.find((m) => m.id === member.id);
								if (localStorageMember) {
									member.selected = localStorageMember.selected;
								}
								return member;
							});

							group.owners = fetchedGroup.owners.map((owner) => {
								const localStorageOwner = localStorageGroup.owners.find((o) => o.id === owner.id);
								if (localStorageOwner) {
									owner.selected = localStorageOwner.selected;
								}
								return owner;
							});

							group.selected = localStorageGroup.selected;
						}
						return group;
					});

					fetchedGroups = await Promise.all(groupPromises);
				}

				setGroups(fetchedGroups);
			}
		} else {
			setGroups(JSON.parse(JSON.stringify(scheduleGroups)));
		}

		setIsloading(false);
	}

	/**
	 * Component mounts and fetches the groups from the redux store or from the database
	**/
	useEffect(() => {
		fetchGroups();

		// We need to set the groups in the redux store when the component unmounts
		return () => {
			dispatch(setAllGroups(groupsRef.current));
		};
	}, []);

	const simplifyGroups = (groups) => {
		if (groups == null) return [];
		return groups.map((group) => {
			const simplifiedGroup = {
				id: group.id,
				title: group.title,
				selected: group.selected,
				members: simplifyUserObjects(group.members),
				owners: simplifyUserObjects(group.owners),
			};
			return simplifiedGroup;
		});
	}

	const simplifyUserObjects = (members) => {
		if (members == null) return [];
		return members.map((member) => {
			const simplifiedMember = {
				id: member.id,
				selected: member.selected,
			};
			return simplifiedMember;
		});
	}

	/**
	 * @param {*} groupId 
	 **/
	const expandGroupView = async (groupId, preventExpand = false, setUserSelected = null) => {
		setGroups((prevGroups) =>
			prevGroups.map((group) =>
				group.id === groupId
					? { ...group, showGroupMembers: preventExpand ? group.showGroupMembers : !group.showGroupMembers, isLoading: true }
					: group
			)
		);

		let updatedGroupObject = await dispatch(getGroupById(groupId, ['owners', 'members']));
		if (updatedGroupObject) {
			// we need to make hard copy of the object to avoid reference issues
			updatedGroupObject = JSON.parse(JSON.stringify(updatedGroupObject));
		}


		setGroups((prevGroups) =>
			prevGroups.map((group) => {
				if (group.id === groupId) {

					// Determine if any group member is already selected
					const hasSelectedStudents = group.members?.some((member) => member.selected) ?? false;

					// Helper to merge an updated owner with the corresponding local owner.
					const mergeOwner = (updatedOwner) => {
						const localOwner = group.owners?.find((o) => o.id === updatedOwner.id);
						// If setUserSelected is provided, use it; otherwise, fallback to the local or updated value.
						const selected = setUserSelected != null && setUserSelected == false ? false : (localOwner?.selected ?? updatedOwner.selected);
						return { ...updatedOwner, selected };
					};

					// Helper to merge an updated member with the corresponding local member.
					const mergeMember = (updatedMember) => {
						const localMember = group.members?.find((m) => m.id === updatedMember.id);
						const selected =
							setUserSelected != null
								? setUserSelected
								: (localMember?.selected ?? updatedMember.selected);
						return { ...updatedMember, selected };
					};

					// Merge owners and members arrays using the helper functions.
					const mergedOwners = (updatedGroupObject.owners || []).map(mergeOwner);
					const mergedMembers = (updatedGroupObject.members || []).map(mergeMember);

					return {
						...group,
						...updatedGroupObject,
						owners: mergedOwners,
						members: mergedMembers,
						isLoading: false,
					};
				}
				return group;
			})
		);
	}

	/**
	 * 
	 * @param {*} searchResponse 
	 * @returns 
	 */
	const handleSearchGroup = async (searchResponse) => {
		let group = searchResponse.group;

		setGroups((prevGroups) => {
			const groupExists = prevGroups.find((g) => g.id === group.id);
			if (groupExists) {
				return prevGroups;
			}
			return [...prevGroups, { ...group, isLoading: true }];
		});

		// Fetch the updated group details from the database
		const updatedGroup = await dispatch(getGroupById(group.id, ['owners', 'members']));

		setGroups((prevGroups) =>
			prevGroups.map((existingGroup) =>
				existingGroup.id === group.id
					? { ...existingGroup, ...updatedGroup, isLoading: false }
					: existingGroup
			)
		);

		return;
	}

	const removeGroup = (groupId) => {
		setGroups((prevGroups) =>
			prevGroups.filter((group) => group.id !== groupId)
		);
	}

	const toggleSelectUser = (isSelected, userId, groupId) => {
		setGroups((prevGroups) =>
			prevGroups.map((group) =>
				group.id === groupId
					? {
						...group,
						owners: group.owners.map((owner) =>
							owner.id === userId ? { ...owner, selected: isSelected } : owner
						),
						members: group.members.map((member) =>
							member.id === userId ? { ...member, selected: isSelected } : member
						),
					}
					: group
			)
		);
	}

	const toggleSelectAll = async (isSelected) => {
		setGroups((prevGroups) =>
			prevGroups.map((group) => ({ ...group, selected: isSelected, isLoading: true }))
		);

		await Promise.all(groups.map(group => expandGroupView(group.id, true, isSelected)));
	}

	const toggleSelectGroup = (isSelected, groupId) => {
		setGroups((prevGroups) =>
			prevGroups.map((group) =>
				group.id === groupId
					? { ...group, isLoading: true }
					: group
			)
		);


		expandGroupView(groupId, true, isSelected);
	}

	/**
	 * 
	 * @param {*} group 
	 * @returns 
	 */
	const renderGroup = (group) => {
		if (group == null) return null;
		return (
			<div className='expendable-Header' key={group.id}>
				<div className='title'>
					<Checkbox
						value={group.owners != null && group.owners.every((owner) => owner.selected) && group.members != null && group.members.every((member) => member.selected)}
						indeterminate={(group.owners != null && group.owners.some((owner) => owner.selected)) || (group.members != null && group.members.some((member) => member.selected))}
						label={group.title}
						onChange={(isSelected) => {
							toggleSelectGroup(isSelected, group.id);
						}}
						disabled={scheduleLoading}
					/>

					{group.userRole && group.userRole === 'OWNER' && (<span className='groupOwner'>
						{translate('Owner')}
					</span>)}

					{!group.userRole && (<button
						className='removeStudentSearch'
						onClick={() => { removeGroup(group.id) }}
					>
						<Icon name='Close' />
					</button>)}
				</div>

				<button
					className='trigger-button'
					onClick={() => expandGroupView(group.id)}
				>
					<span className='trigger'>
						{group.showGroupMembers ? <Minus /> : <Plus />}
					</span>
				</button>

				<div className='subSection'>
					{group.showGroupMembers && (
						<Fragment>
							{group.isLoading && (
								<div className='listLoader'>
									<Spinner small center />
								</div>
							)}

							{!group.isLoading && group.owners.map((owner, index) => {
								return (
									<div key={owner.id}>
										<div className='title'>
											<Checkbox
												value={owner.selected}
												onChange={(selected) => {
													toggleSelectUser(selected, owner.id, group.id);
												}}
												label={(
													<span>
														{owner.firstName}{' '}
														{owner.lastName}

														<span className='groupOwner'>
															{translate('Owner')}
														</span>
													</span>
												)}
												disabled={scheduleLoading}
											/>
										</div>
									</div>
								)
							})}


							{!group.isLoading && group.members.map((member, index) => {
								return (
									<div key={member.id}>
										<div className='title'>
											<Checkbox
												value={member.selected}
												onChange={(selected) => {
													toggleSelectUser(selected, member.id, group.id);
												}}
												label={
													<DisplayName
														firstName={member.firstName}
														lastName={member.lastName}
														email={member.email}
														data={group?.members}
													/>
												}
												disabled={scheduleLoading}
											/>
										</div>
									</div>
								)
							})}
						</Fragment>
					)}
				</div>

			</div>
		);
	}


	const myGroups = useMemo(() => {
		if (groups == null) return [];
		return groups.filter((group) => group.userRole);
	}, [groups]);

	const otherGroups = useMemo(() => {
		if (groups == null) return [];
		return groups.filter((group) => !group.userRole);
	}, [groups]);

	const totalSelectedMembers = useMemo(() => {
		if (groups == null) return 0;
		const uniqueMembers = new Set();
		groups.forEach(group => {
			if (group.members) {
				group.members.forEach(member => {
					if (member.selected) {
						uniqueMembers.add(member.id);
					}
				});
			}
		});
		return uniqueMembers.size;
	}, [groups]);

	const totalSelectedOwners = useMemo(() => {
		if (groups == null) return 0;
		const uniqueOwners = new Set();
		groups.forEach(group => {
			if (group.owners) {
				group.owners.forEach(owner => {
					if (owner.selected) {
						uniqueOwners.add(owner.id);
					}
				});
			}
		});
		return uniqueOwners.size;
	}, [groups]);

	console.log('loader', isLoading)

	return (
		<div>
			<DropDownList
				label={`${translate('Displaying')} ${totalSelectedMembers} ${translate('students').toLowerCase()}, ${totalSelectedOwners} ${translate('owners').toLowerCase()}`}
				offset={15}
				placement={'bottom'}
			>

				{/* Monitor the mounting/unmounting of the dropdown content so we can set z-index correctly*/}
				<DropDownContentVisiblityMonitor />

				{isLoading && <div className='listLoader'>
					<Spinner small center />
				</div>}

				{!isLoading && groups != null && (
					<div>
						<Fragment>
							<div className='toolTip_groups_header'>
								<div>
									<span>
										{`${translate('Students')}: ${totalSelectedMembers}`}
									</span>
								</div>

								<div>
									<span>
										{`${translate('Owner')}: ${totalSelectedOwners}`}
									</span>
								</div>
							</div>

							<Checkbox
								onChange={toggleSelectAll}
								value={myGroups.every(group => group.selected)}
								label={translate('select-all')}
							/>

							<div className="expendable-Header">
								<span style={{ fontSize: 'var(--size-14)', fontWeight: 500 }}>
									{translate('My groups')}
								</span>
							</div>

							{myGroups.map((group, index) => renderGroup(group))}

							<div className='searchOtherGroups'>
								<div className="expendable-Header">
									<span style={{ fontSize: 'var(--size-14)', fontWeight: 500 }}>
										{translate('Other groups')}
									</span>
								</div>

								{otherGroups.map((group, index) => renderGroup(group))}

								<Collapsible trigger={translate('Add group')}>
									<SearchGroups onSubmit={handleSearchGroup} />
								</Collapsible>
							</div>
						</Fragment>
					</div>
				)}

			</DropDownList>

			{totalSelectedMembers > 0 || totalSelectedOwners > 0 ? (
				<div className="clear-filter-container" onClick={() => {
					if (!isLoading) {
						toggleSelectAll(false);
						dispatch(setAllGroups([]));
					}
				}}>
					<Icon name="Close" /> {translate('clear-filter')}
				</div>
			) : null}
		</div>
	);
};

const DropDownContentVisiblityMonitor = () => {
	useEffect(() => {
		let element = document.querySelector('.c--content');
		if (element) {
			element.style.zIndex = 2;
		}
		return () => {
			let element = document.querySelector('.c--content');
			if (element) {
				element.style.zIndex = 1;
			}
		}
	}, []);

	return null; // This component doesn't render any visible UI.
};

export default GroupsAndUserSelector;